import { FC, ReactNode, useEffect } from 'react'
import { Col, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import shallow from 'zustand/shallow'

import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { SettingsFormFields } from './types'
import { useSettingsModalStore } from './SettingsModalStore'
import { SettingsModalLocalePreview } from './SettingsModalLocalePreview'
import { Radio } from 'antd'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import moment from 'moment-timezone'
import { getCountryByName } from 'country-locale-map'
import PerButton from '../Button'

const { Option } = Select

export const SettingsModalContent: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [locales, setSettingsForm] = useSettingsModalStore((state) => [state.locales, state.setSettingsForm], shallow)

  const settingsForm = useForm<SettingsFormFields>({
    defaultValues: {
      locale: user.locale,
      timezone: user.timezone,
      nameDisplayPreference: user.nameDisplayPreference,
    },
    criteriaMode: 'all',
    mode: 'onChange',
  })
  const { control, watch, getValues, setValue } = settingsForm

  watch(['locale', 'timezone'])

  const renderLocales = (): ReactNode[] | null => {
    if (!!locales && locales.length) {
      return locales.map((locale) => (
        <Option key={locale.country} value={locale.locale}>
          {locale.country}
        </Option>
      ))
    }
    return null
  }

  const suggestedTimezone = moment.tz.guess()

  const timezones = [
    ...new Set(
      locales.reduce((timezones: string[], locale) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return timezones.concat(moment.tz.zonesForCountry(getCountryByName(locale.country)!.alpha2))
      }, []),
    ),
  ].sort()

  const getTimezoneOffsets = (timezone: string) => {
    // calculate DST and non DST offset for given timezone
    const summerOffset = moment.tz({ month: 0, day: 1 }, timezone).format('Z')
    const winterOffset = moment.tz({ month: 5, day: 1 }, timezone).format('Z')
    return summerOffset !== winterOffset ? [summerOffset, winterOffset] : [summerOffset]
  }

  const renderTimezones = (): ReactNode[] | null => {
    return timezones.map((timezone) => (
      <Option key={timezone} value={timezone}>
        <PerFlexRow justify="space-between">
          <PerText>{`${timezone}`}</PerText>
          <PerText>
            {getTimezoneOffsets(timezone)
              .reduce((offsets, offset) => {
                return (
                  offsets + t('settingsModal.timezone.timezoneOffset', { offset: offset }) + t('settingsModal.timezone.offsetsSeparator')
                )
              }, '')
              .slice(0, -t('settingsModal.timezone.offsetsSeparator').length)}
          </PerText>
        </PerFlexRow>
      </Option>
    ))
  }

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
  }

  useEffect(() => {
    setSettingsForm(settingsForm)
  }, [settingsForm, setSettingsForm])

  return (
    <>
      <PerFlexRow mb="xs">
        <PerText fontSize="m" fontWeight="bold">
          {t('settingsModal.timezoneAndLocale')}
        </PerText>
      </PerFlexRow>
      <PerFlexRow mb="m">
        <PerText fontSize="s" color="secondary">
          {t('settingsModal.description')}
        </PerText>
      </PerFlexRow>
      <PerFlexRow align="middle" mb="m">
        <Col span={4}>
          <PerText color="secondary">{t('settingsModal.timezone.label')}</PerText>
        </Col>
        <Col span={20}>
          <Controller
            name="timezone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                onChange={(value: string) => {
                  onChange(value)
                }}
                value={value}
                disabled={!timezones || !timezones.length}
                style={{ width: '100%' }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {timezones.includes(suggestedTimezone) && (
                      <PerButton
                        onMouseDown={() => {
                          setValue('timezone', suggestedTimezone)
                        }}
                        type="button"
                        width="fullWidth"
                        className="per-btn--white"
                        my="xxxs"
                        text={t('settingsModal.timezone.suggestedTimezoneButton', { timezone: suggestedTimezone })}
                      />
                    )}
                  </>
                )}
              >
                {renderTimezones()}
              </Select>
            )}
          />
        </Col>
      </PerFlexRow>
      <PerFlexRow align="middle" mb="m">
        <Col span={4}>
          <PerText color="secondary">{t('settingsModal.locale')}</PerText>
        </Col>
        <Col span={20}>
          <Controller
            name="locale"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                data-testid="locale-dropdown"
                onChange={(value: string) => {
                  onChange(value)
                }}
                value={value}
                disabled={!locales || !locales.length}
                style={{ width: '100%' }}
              >
                {renderLocales()}
              </Select>
            )}
          />
        </Col>
      </PerFlexRow>
      <SettingsModalLocalePreview
        timezone={getValues().timezone || user.timezone}
        locale={getValues().locale || user.locale}
        currency={user.currencyForPayments}
      />

      <PerFlexRow mb="xs" mt="l">
        <PerText fontSize="m" fontWeight="bold">
          {t('settingsModal.nameDisplayPreference.title')}
        </PerText>
      </PerFlexRow>
      <PerFlexRow mb="m">
        <PerText fontSize="s" color="secondary">
          {t('settingsModal.nameDisplayPreference.description')}
        </PerText>
      </PerFlexRow>
      <PerFlexRow align="middle" mb="m">
        <Col>
          <Controller
            name="nameDisplayPreference"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Radio.Group onChange={(e) => onChange(e.target.value)} value={value}>
                <Radio style={radioStyle} value={NameDisplayPreference.NAME_FIRST}>
                  {NameDisplayPreference.NAME_FIRST}
                </Radio>
                <Radio style={radioStyle} value={NameDisplayPreference.SURNAME_FIRST}>
                  {NameDisplayPreference.SURNAME_FIRST}
                </Radio>
              </Radio.Group>
            )}
          />
        </Col>
      </PerFlexRow>
    </>
  )
}
